import { useEffect, useId, useState, type CSSProperties } from "react";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { useBoard } from "@/hooks/useBoard";
import {
  COLUMN_TYPES,
  type ColumnConfig,
  type ColumnType,
  type StatusRole,
  type UUID,
} from "@/lib/types";
import { STATUS_ROLES, STATUS_ROLE_LABELS } from "@/lib/statusRoles";
import { STATUS_COLORS, statusColorOf } from "@/lib/tokens";
import { t } from "@/lib/strings";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID;
}

interface StatusOptionDraft {
  id: string;
  label: string;
  color: string;
  // "" = plain option with no workflow meaning (the common case for non-ticket
  // status columns). A role makes the option a target for ticket actions.
  role: StatusRole | "";
}

// Every palette colour, derived from STATUS_PALETTE so the picker + the round-robin
// default can't drift from the real set (they once silently omitted gray + yellow).
const STATUS_PALETTE = STATUS_COLORS;

function slug(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32) || `opt-${Math.random().toString(36).slice(2, 6)}`
  );
}

const TYPE_LABELS: Record<ColumnType, string> = {
  text: "Text (kurz)",
  long_text: "Text (lang)",
  status: "Status",
  person: "Person",
  date: "Datum",
  timeline: "Zeitraum",
  number: "Zahl",
  checkbox: "Checkbox",
  tags: "Schlagwörter",
  link: "Link",
  file: "Datei",
  lead_time: "Vorlaufzeit",
  // Read-only ticket meta — labelled for completeness; not offered in the
  // picker (absent from COLUMN_TYPES).
  creator: "Ersteller",
  creator_department: "Abteilung",
  creator_company: "Unternehmen",
};

export function AddColumnDialog({ open, onOpenChange, boardId }: Props) {
  const nameId = useId();
  const typeId = useId();
  const { createColumn } = useBoard(boardId);
  const [name, setName] = useState("");
  const [type, setType] = useState<ColumnType>("text");
  const [options, setOptions] = useState<StatusOptionDraft[]>([
    { id: "offen", label: "Offen", color: "red", role: "open" },
    { id: "in-bearbeitung", label: "In Bearbeitung", color: "blue", role: "in_progress" },
    { id: "geschlossen", label: "Geschlossen", color: "green", role: "closed" },
  ]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setName("");
      setType("text");
      setError(null);
      createColumn.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !createColumn.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);

    let config: ColumnConfig | null = null;
    if (type === "status") {
      const cleanOpts = options
        .map((o) => ({ ...o, label: o.label.trim() }))
        .filter((o) => o.label.length > 0)
        .map((o) => ({
          id: o.id || slug(o.label),
          label: o.label,
          color: o.color,
          ...(o.role ? { role: o.role } : {}),
        }));
      if (cleanOpts.length === 0) {
        setError("Bitte mindestens eine Status-Option anlegen.");
        return;
      }
      config = { options: cleanOpts };
    }

    try {
      await createColumn.mutateAsync({ name: trimmed, type, config });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.unknownError);
    }
  }

  function setOpt(idx: number, patch: Partial<StatusOptionDraft>) {
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  }
  function removeOpt(idx: number) {
    setOptions((prev) => prev.filter((_, i) => i !== idx));
  }
  function addOpt() {
    setOptions((prev) => [
      ...prev,
      {
        id: "",
        label: "",
        color: STATUS_PALETTE[prev.length % STATUS_PALETTE.length] ?? "slate",
        role: "",
      },
    ]);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.addColumnTitle}
      description={t.addColumnDescription}
      width={520}
    >
      <form onSubmit={onSubmit}>
        <DialogField label={t.columnName} htmlFor={nameId} error={error ?? undefined}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.columnNamePlaceholder}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
          />
        </DialogField>

        <DialogField label={t.columnType} htmlFor={typeId}>
          <select
            id={typeId}
            value={type}
            onChange={(e) => setType(e.target.value as ColumnType)}
            style={{ ...dialogInputStyle, height: 36 }}
          >
            {COLUMN_TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {TYPE_LABELS[tp]}
              </option>
            ))}
          </select>
        </DialogField>

        {type === "status" ? (
          <div style={{ marginBottom: 14 }}>
            <div style={statusLabelStyle}>{t.statusOptions}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {options.map((o, i) => (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <ColorSwatch value={o.color} onChange={(c) => setOpt(i, { color: c })} />
                  <input
                    value={o.label}
                    onChange={(e) =>
                      setOpt(i, { label: e.target.value, id: o.id || slug(e.target.value) })
                    }
                    placeholder="z. B. Wartet auf Kunde"
                    style={{ ...dialogInputStyle, flex: 1 }}
                  />
                  <select
                    value={o.role}
                    onChange={(e) => setOpt(i, { role: e.target.value as StatusRole | "" })}
                    title={t.statusOptionRole}
                    style={{ ...dialogInputStyle, height: 36, width: 132, flexShrink: 0 }}
                  >
                    <option value="">{t.statusRoleNone}</option>
                    {STATUS_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {STATUS_ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                  <IconButton
                    icon="x"
                    size={28}
                    title={t.removeOption}
                    onClick={() => removeOpt(i)}
                  />
                </div>
              ))}
              <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>
                {t.statusRoleHint}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon="plus"
                onClick={addOpt}
                style={{ alignSelf: "flex-start", marginTop: 4 }}
              >
                {t.addOption}
              </Button>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={createColumn.isPending}
          >
            {t.cancel}
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={!canSubmit}>
            {createColumn.isPending ? t.creating : t.create}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export function ColorSwatch({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t.statusOptionColor}
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          border: "1px solid var(--hairline-strong)",
          background: colorPreview(value),
          cursor: "pointer",
          padding: 0,
        }}
      />
      {open ? (
        <div
          style={{
            position: "absolute",
            top: 32,
            left: 0,
            zIndex: 10,
            background: "var(--surface-elev)",
            border: "1px solid var(--hairline-strong)",
            borderRadius: 8,
            padding: 6,
            display: "grid",
            gridTemplateColumns: "repeat(4, 26px)",
            gap: 4,
            boxShadow: "0 8px 20px rgba(13,62,99,0.12)",
          }}
        >
          {STATUS_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                border: c === value ? "2px solid var(--ink-1)" : "1px solid var(--hairline)",
                background: colorPreview(c),
                cursor: "pointer",
                padding: 0,
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function colorPreview(name: string): string {
  // The swatch shows the palette's saturated `dot` hue — resolved from the single
  // source (statusColorOf) so a new colour needs no second edit here.
  return statusColorOf(name).dot;
}

const statusLabelStyle: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--ink-2)",
  marginBottom: 5,
};
